<section class="users-page">
  <div class="page-heading">
    <div>
      <p>{{ isPlatformAdminView ? 'Platform access' : 'Team management' }}</p>
      <h2>{{ isPlatformAdminView ? 'Platform admin users' : 'Users and role assignment' }}</h2>
    </div>
  </div>

  @if ((!isPlatformAdminView || managesPlatformUsers) && canInviteUsers) {
    <section class="invite-panel">
      <h3>{{ managesPlatformUsers ? 'Create platform admin user' : 'Add user' }}</h3>
      @if (managesPlatformUsers) { <p class="state">Assign only the job role required for this person. Super Admin roles cannot be assigned here.</p> }
      <form (ngSubmit)="inviteUser()">
        <input name="name" [(ngModel)]="invite.name" placeholder="Full name" />
        <input type="email" name="email" [(ngModel)]="invite.email" placeholder="Email address" />
        <input type="datetime-local" name="accessExpiresAt" [(ngModel)]="invite.accessExpiresAt" aria-label="Optional access expiry" title="Optional access expiry" />
        @if (!isPlatformAdminView) {
        <fieldset class="location-access">
          <legend>Roles (select one or more)</legend>
          <div class="location-options">
            @for (role of roleRecords; track role.id) {
              <label class="location-option role-option">
                <input type="checkbox" [checked]="invite.roleIds.includes(role.id)" (change)="toggleInviteRole(role.id, $any($event.target).checked)" />
                <span class="role-copy"><strong>{{ role.name }}</strong><small>{{ role.isSystemRole ? 'Default role' : 'Custom role' }}</small></span>
                <button class="permission-info" type="button" title="View permissions" [attr.aria-label]="'View permissions for ' + role.name" (click)="openRolePermissions($event, role)">
                  <span class="material-symbols-outlined" aria-hidden="true">info</span>
                </button>
              </label>
            }
          </div>
        </fieldset>
        <fieldset class="location-access">
          <legend>Business location access (select one or more)</legend>
          @if (isLocationsLoading) {
            <span class="location-state">Loading locations...</span>
          } @else {
            <div class="location-options">
              @for (location of locations; track location.id) {
                <label class="location-option">
                  <input
                    type="checkbox"
                    [checked]="isLocationSelected(location.id)"
                    (change)="toggleLocation(location.id, $any($event.target).checked)"
                  />
                  <span>
                    <strong>{{ location.name }}</strong>
                    <small>{{ location.city || location.area || 'Address unavailable' }}</small>
                  </span>
                </label>
              } @empty {
                <span class="location-state">No active business locations found.</span>
              }
            </div>
          }
        </fieldset>
        }
        <button
          type="submit"
          [disabled]="isSubmitting || (!isPlatformAdminView && (isLocationsLoading || locations.length === 0))"
        >
          <span class="material-symbols-outlined" aria-hidden="true">person_add</span>
          {{ isSubmitting ? 'Inviting...' : 'Invite user' }}
        </button>
      </form>
    </section>
  }


  @if (isPlatformAdminView) {
    <div class="mode-tabs" aria-label="Filter platform users by status">
      <button type="button" [class.active]="adminStatusFilter === 'all'" (click)="setAdminStatusFilter('all')">All</button>
      <button type="button" [class.active]="adminStatusFilter === 'active'" (click)="setAdminStatusFilter('active')">Active</button>
      <button type="button" [class.active]="adminStatusFilter === 'inactive'" (click)="setAdminStatusFilter('inactive')">Inactive</button>
    </div>
  }

  @if (isLoading) {
    <p class="state">Loading users...</p>
  }

  @if (successMessage) {
    <p class="state success">{{ successMessage }}</p>
  }

  @if (errorMessage) {
    <p class="state error">{{ errorMessage }}</p>
  }

  <section class="table-panel">
    <div class="panel-title">
      <h3>{{ isPlatformAdminView ? 'Platform admin users' : 'Business users' }}</h3>
      <button class="secondary" type="button">
        <span class="material-symbols-outlined" aria-hidden="true">download</span>
        Export
      </button>
    </div>
    <div class="table-wrap">
      <table>
        <thead>
          <tr>
            <th>User</th>
            <th>{{ isPlatformAdminView ? 'Scope' : 'Business locations' }}</th>
            <th>Role</th>
            <th>Status</th>
            <th>Access expiry</th>
            @if (!isPlatformAdminView || managesPlatformUsers) {
              <th>Action</th>
            }
          </tr>
        </thead>
        <tbody>
          @for (user of users; track user.email) {
            <tr>
              <td>
                <strong>{{ user.name }}</strong>
                <small>{{ user.email }}</small>
              </td>
              <td>{{ isPlatformAdminView ? 'Platform-wide' : userLocationSummary(user) }}</td>
              <td>
                @if (isPlatformAdminView) {
                  {{ (user.roles ?? [user.role]).join(', ') || (managesPlatformUsers ? 'Unassigned' : 'Business Owner') }}
                } @else {
                  {{ (user.roles ?? [user.role]).join(', ') }}
                }
              </td>
              <td>
                <span class="status" [class.inactive]="userStatus(user) === 'Inactive'" [class.expired]="userStatus(user) === 'Expired'" [class.invited]="userStatus(user) === 'Invited'">{{ userStatus(user) }}</span>
              </td>
              <td>
                @if (isMainOwner(user) || isProtectedPlatformAdmin(user)) {
                  <span class="owner-protected">{{ expiryLabel(user) }}</span>
                } @else if (canUpdateUsers && accessEditingUserId === user.id) {
                  <div class="expiry-editor">
                    <input type="datetime-local" [(ngModel)]="accessExpiryValue" aria-label="User access expiry" />
                    <button class="icon-action save" type="button" title="Save expiry" [disabled]="processingUserId === user.id" (click)="saveExpiry(user)"><span class="material-symbols-outlined">check</span></button>
                    <button class="icon-action" type="button" title="Cancel" (click)="accessEditingUserId = ''"><span class="material-symbols-outlined">close</span></button>
                  </div>
                } @else if (canUpdateUsers) {
                  <button class="expiry-value" type="button" title="Set access expiry" (click)="beginAccessEdit(user)">{{ expiryLabel(user) }}</button>
                } @else {
                  <span>{{ expiryLabel(user) }}</span>
                }
              </td>
              @if (!isPlatformAdminView || managesPlatformUsers) {
                <td>
                  @if (isMainOwner(user) || isProtectedPlatformAdmin(user)) {
                    <span class="owner-protected"><span class="material-symbols-outlined">verified_user</span>{{ isProtectedPlatformAdmin(user) ? 'Protected admin' : 'Main owner protected' }}</span>
                  } @else {
                    <div class="user-actions">
                      @if (canAssignRoles) { <button class="icon-action" type="button" title="Edit user role" (click)="beginEdit(user)"><span class="material-symbols-outlined">manage_accounts</span></button> }
                      @if (canUpdateUsers) { <button class="icon-action" type="button" [title]="user.status === 'Suspended' ? 'Activate user' : 'Deactivate user'" [disabled]="processingUserId === user.id" (click)="toggleUserStatus(user)"><span class="material-symbols-outlined">{{ user.status === 'Suspended' ? 'person_check' : 'person_off' }}</span></button> }
                      @if (user.mustChangePassword && canInviteUsers) {
                        <button class="icon-action invite" type="button" title="Resend invitation" [disabled]="processingUserId === user.id" (click)="resendInvite(user)"><span class="material-symbols-outlined">forward_to_inbox</span></button>
                      }
                      @if (canDeleteUsers) { <button class="icon-action danger" type="button" title="Remove user" [disabled]="processingUserId === user.id" (click)="openDeleteUser(user)"><span class="material-symbols-outlined">delete</span></button> }
                    </div>
                  }
                </td>
              }
            </tr>
          }
        </tbody>
      </table>
    </div>
  </section>
  @if (isLoadingMore) {
    <p class="state">
      Loading 10 more users…
    </p>
  }

  @if (editingUser) {
    <div class="dialog-backdrop" role="presentation" (click)="closeUserEdit()">
      <section class="permission-dialog user-role-dialog" role="dialog" aria-modal="true" [attr.aria-label]="'Edit role for ' + editingUser!.name" (click)="$event.stopPropagation()">
        <header>
          <div>
            <span class="dialog-eyebrow">User access</span>
            <h3>Edit {{ editingUser!.name }}</h3>
            <p>Change assigned roles and business locations from this window.</p>
          </div>
          <button class="dialog-close" type="button" title="Close" aria-label="Close" (click)="closeUserEdit()"><span class="material-symbols-outlined">close</span></button>
        </header>

        <div class="role-dialog-section">
          <h4>Assigned roles</h4>
          <div class="location-options">
            @for (role of roleRecords; track role.id) {
              <label class="location-option role-option">
                <input type="checkbox" [checked]="editingRoleIds.includes(role.id)" (change)="toggleEditingRole(role.id, $any($event.target).checked)" />
                <span class="role-copy"><strong>{{ role.name }}</strong><small>{{ role.description || (role.isSystemRole ? 'Default role' : 'Custom role') }}</small></span>
                <button class="permission-info" type="button" title="View role permissions" (click)="openRolePermissions($event, role)"><span class="material-symbols-outlined">info</span></button>
              </label>
            }
          </div>
        </div>

        @if (!isPlatformAdminView) {
          <div class="role-dialog-section">
            <h4>Business locations</h4>
            <div class="location-options">
              @for (location of locations; track location.id) {
                <label class="location-option">
                  <input type="checkbox" [checked]="editingLocationIds.includes(location.id)" (change)="toggleEditingLocation(location.id, $any($event.target).checked)" />
                  <span><strong>{{ location.name }}</strong><small>{{ location.city || location.area || 'Address unavailable' }}</small></span>
                </label>
              }
            </div>
          </div>
        }

        <footer class="dialog-actions">
          <button class="secondary" type="button" [disabled]="isSubmitting" (click)="closeUserEdit()">Cancel</button>
          <button type="button" [disabled]="isSubmitting" (click)="updateRole(editingUser!)"><span class="material-symbols-outlined">save</span>{{ isSubmitting ? 'Saving…' : 'Save role access' }}</button>
        </footer>
      </section>
    </div>
  }

  @if (deletingUser) {
    <div class="dialog-backdrop" role="presentation" (click)="closeDeleteUser()">
      <section class="permission-dialog delete-user-dialog" role="alertdialog" aria-modal="true" aria-labelledby="delete-user-title" (click)="$event.stopPropagation()">
        <header>
          <div>
            <span class="danger-symbol material-symbols-outlined" aria-hidden="true">person_remove</span>
            <span class="dialog-eyebrow danger-copy">Permanent action</span>
            <h3 id="delete-user-title">Remove {{ deletingUser.name }}?</h3>
            <p>This permanently removes the account and cannot be undone. The action will be stored in the audit trail.</p>
          </div>
          <button class="dialog-close" type="button" title="Close" aria-label="Close" [disabled]="processingUserId === deletingUser.id" (click)="closeDeleteUser()"><span class="material-symbols-outlined">close</span></button>
        </header>
        <div class="delete-confirmation">
          <label for="delete-confirmation">Type <strong>{{ deletingUser.email }}</strong> to confirm</label>
          <input id="delete-confirmation" name="deleteConfirmation" [(ngModel)]="deleteConfirmationText" autocomplete="off" placeholder="Enter the user's email address" />
        </div>
        <footer class="dialog-actions">
          <button class="secondary" type="button" [disabled]="processingUserId === deletingUser.id" (click)="closeDeleteUser()">Cancel</button>
          <button class="delete-confirm-button" type="button" [disabled]="processingUserId === deletingUser.id || deleteConfirmationText.trim().toLowerCase() !== deletingUser.email.trim().toLowerCase()" (click)="confirmDeleteUser()">
            <span class="material-symbols-outlined">delete_forever</span>{{ processingUserId === deletingUser.id ? 'Removing…' : 'Permanently remove user' }}
          </button>
        </footer>
      </section>
    </div>
  }

  @if (permissionRole) {
    <div class="dialog-backdrop" role="presentation" (click)="closeRolePermissions()">
      <section class="permission-dialog" role="dialog" aria-modal="true" [attr.aria-label]="permissionRole.name + ' permissions'" (click)="$event.stopPropagation()">
        <header>
          <div>
            <span class="dialog-eyebrow">Role access</span>
            <h3>{{ permissionRole.name }}</h3>
            <p>{{ permissionRole.description || 'Permissions available to users assigned this role.' }}</p>
          </div>
          <button class="dialog-close" type="button" aria-label="Close permissions" title="Close" (click)="closeRolePermissions()">
            <span class="material-symbols-outlined" aria-hidden="true">close</span>
          </button>
        </header>

        @if (isRolePermissionsLoading) {
          <div class="permission-loading"><span class="permission-spinner"></span><strong>Loading permissions…</strong></div>
        } @else {
          <div class="permission-groups">
            @for (group of groupedRolePermissions; track group.module) {
              <section class="permission-group">
                <h4>{{ group.module }}</h4>
                @for (permission of group.permissions; track permission.id) {
                  <div class="permission-row">
                    <span class="material-symbols-outlined" aria-hidden="true">check_circle</span>
                    <div><strong>{{ permission.name }}</strong><small>{{ permission.description || permission.code }}</small></div>
                  </div>
                }
              </section>
            } @empty {
              <div class="permission-empty"><span class="material-symbols-outlined" aria-hidden="true">lock</span><strong>No permissions assigned</strong><small>This role currently does not grant access to any module.</small></div>
            }
          </div>
        }
      </section>
    </div>
  }
</section>
